"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getThoughts } from "@/features/thoughts/api/getThoughts";
import { THOUGHT_QUERY_DEFAULTS } from "@/features/thoughts/config/thoughtQueryScopes";
import { useMeiliRefreshState } from "@/state/globalStore";
import { useAuth } from "@/state/globalStore";
import { useSituationsQuery } from "@/features/situations";

const needsAnchorKeys = {
  count: (
    validSituationIds: string[],
    refreshStamp: {
      confirmed: string;
    },
  ) =>
    [
      "profile-shaping",
      "needs-anchor",
      "count",
      validSituationIds,
      refreshStamp,
    ] as const,
};

function countThoughtsThatNeedAnchor(
  thoughts: Awaited<ReturnType<typeof getThoughts>>,
  validSituationIds: string[],
) {
  if (validSituationIds.length === 0) {
    return thoughts.length;
  }

  const validSituationIdSet = new Set(validSituationIds);

  return thoughts.filter(
    (thought) =>
      !thought.situation_id || !validSituationIdSet.has(thought.situation_id),
  ).length;
}

export default function useNeedsAnchorCount() {
  const { accessToken } = useAuth();
  const situationsQuery = useSituationsQuery();
  const { meiliRefreshStamp } = useMeiliRefreshState();

  const validSituationIds = useMemo(
    () =>
      (situationsQuery.data ?? [])
        .map((situation) => situation.id)
        .sort((a, b) => a.localeCompare(b)),
    [situationsQuery.data],
  );
  const needsAnchorRefreshStamp = useMemo(
    () => ({
      confirmed: Object.values(meiliRefreshStamp["studio_thoughts"]).join(":"),
    }),
    [meiliRefreshStamp],
  );

  const countQuery = useQuery({
    queryKey: needsAnchorKeys.count(
      validSituationIds,
      needsAnchorRefreshStamp,
    ),
    queryFn: async () => {
      if (!accessToken) {
        return 0;
      }

      const confirmedThoughts = await getThoughts(accessToken, {
        select_by_confirmation_status: THOUGHT_QUERY_DEFAULTS.confirmationStatus,
      });

      return countThoughtsThatNeedAnchor(confirmedThoughts, validSituationIds);
    },
    enabled: Boolean(accessToken) && situationsQuery.isSuccess,
    staleTime: 30_000,
    retry: false,
  });

  return {
    count: countQuery.data,
    hasKnownCount:
      typeof countQuery.data === "number" &&
      !countQuery.isError &&
      !situationsQuery.isError,
    isLoading: situationsQuery.isLoading || countQuery.isLoading,
    isError: situationsQuery.isError || countQuery.isError,
  };
}
